import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddNewPaymentModal from "./AddNewPayment.jsx";
import {
  getPaymentProfiles,
  clearSuccessMessage,
  resetPaymentData,
  makeOnetimePayment,
  setMakePaymentAmount,
} from "../../../../app/redux/slices/app/paymentSlice.js";
import CustomSnackbar from "../../../components/customComponents/CustomSnackbar/index";
import Collapse from "@mui/material/Collapse";
import { Skeleton, Card, CardContent, Alert } from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import IconButton from "@mui/material/IconButton";
import { CircularProgress } from "@mui/material";
import VoiceButton from "../../../../features/components/customComponents/CustomVoiceButton.js";
import VoiceTextbox from "../../../../features/components/customComponents/CustomVoiceTextBox.js";
import { UserTypes } from "../../../../features/constants/commonEnum.js";

const MakePayment = ({
  amount,
  paymentProfileId,
  parentAccountNumber,
  service_type,
  serviceAccountNumber,
  billingAccountNumber,
  state,
  onClose,
}) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [payementSnackBar, setPayementSnackBar] = useState({
    type: "success",
    message: "",
    open: false,
  });
  const [permanentMessage, setPermanentMessage] = useState({
    type: "",
    message: "",
    show: false,
  });
  const [countdown, setCountdown] = useState(15);
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(true);
  const [openCollapse, setOpenCollapse] = useState(false);
  const [paymentInProgress, setPaymentInProgress] = useState("");
  const { userAccountDetails } = useSelector((state) => state?.userDetailSlice);

  const cardtype = [
    {
      name: "MC",
      image: "mc",
    },
    {
      name: "MC_DEBIT",
      image: "mc",
    },
    {
      name: "VISA",
      image: "visa",
    },
    {
      name: "VISA_DEBIT",
      image: "visa",
    },
    {
      name: "AMAZON_PAY",
      image: "amazone",
    },
    {
      name: "VENMO",
      image: "venmo",
    },
    {
      name: "DISC",
      image: "discover",
    },
    {
      name: "DISC_DEBIT",
      image: "discover",
    },
    {
      name: "AP",
      image: "applePay",
    },
    {
      name: "GP",
      image: "gpay",
    },
    {
      name: "AMEX",
      image: "amex",
    },
    {
      name: "PAYPAY_ACCOUNT",
      image: "paypal",
    },
    {
      name: "PAYPAY_CREDIT",
      image: "paypal",
    },
  ];

  const dispatch = useDispatch();
  const paymenProfileData = useSelector(
    (state) => state?.payementSlice.paymentProfileData,
  );
  const triggerPayment = useSelector(
    (state) => state?.payementSlice.triggerPayment,
  );
  const paymenProfileDataDelete = useSelector(
    (state) => state?.payementSlice.paymentProfileDataDelete,
  );
  const makePaymentAmount = useSelector(
    (state) => state?.payementSlice.makePaymentAmount,
  );

  const lastPaymentAttemptRef = useRef(0);
  const processingRef = useRef(false);
  const closeTimeoutRef = useRef(null);
  // useEffect(() => {
  //   if (triggerPayment && paymenProfileData?.data?.length === 0) {
  //     setOpenCollapse(true);
  //   }
  // }, [triggerPayment, paymenProfileData?.data]);

  useEffect(() => {
    dispatch(
      getPaymentProfiles({
        crm_customer_id: paymentProfileId,
        crm_service_account_id: serviceAccountNumber,
        crm_billing_account_id: billingAccountNumber,
        parent_account_number: parentAccountNumber,
      }),
    );
  }, [
    billingAccountNumber,
    dispatch,
    parentAccountNumber,
    serviceAccountNumber,
    paymentProfileId,
  ]);

  useEffect(() => {
    if (amount) {
      dispatch(setMakePaymentAmount(Number(amount?.toFixed(2))));
    }
  }, [amount]);

  useEffect(() => {
    dispatch(resetPaymentData());
    clearPermanentMessage();

    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (paymenProfileDataDelete.successMessage) {
      setOpenSnackbar(true);
    }
  }, [paymenProfileDataDelete]);

  // Countdown timer for success messages
  useEffect(() => {
    let timer;
    if (
      permanentMessage.show &&
      permanentMessage.type === "success" &&
      countdown > 0 &&
      autoCloseEnabled
    ) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }

    // Auto close when countdown reaches 0
    if (
      countdown === 0 &&
      permanentMessage.type === "success" &&
      autoCloseEnabled
    ) {
      if (onClose) {
        clearPermanentMessage();
        setPaymentInProgress("");
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
        onClose();
      }
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [
    permanentMessage.show,
    permanentMessage.type,
    countdown,
    autoCloseEnabled,
    onClose,
  ]);

  const clearPermanentMessage = () => {
    setPermanentMessage({
      type: "",
      message: "",
      show: false,
    });
    setCountdown(15);
    setAutoCloseEnabled(true);
  };

  const handleStopAutoClose = () => {
    setAutoCloseEnabled(false);
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleCloseNow = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (onClose) {
      clearPermanentMessage();
      setPaymentInProgress("");
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      onClose();
    }
  };

  const handlePaymentSuccess = (successAmount) => {
    const successMsg = `Your last payment of $${successAmount} was successful. It may take a few minutes for the changes to reflect in your balance.`;

    setPayementSnackBar({
      type: "success",
      open: true,
      message: successMsg,
    });

    setPermanentMessage({
      type: "success",
      message: successMsg,
      show: true,
    });

    setCountdown(10);
    setAutoCloseEnabled(true);

    // Clear the amount to hide pay buttons
    dispatch(setMakePaymentAmount(""));

    // Stop spinner immediately on success
    setPaymentInProgress("");
    processingRef.current = false;
  };

  const handlePaymentError = (errorMessages) => {
    const errorMsg =
      Array.isArray(errorMessages) && errorMessages.length > 0
        ? errorMessages.join(". ")
        : "Error in completing payment.";

    setPayementSnackBar({
      type: "error",
      open: true,
      message: errorMsg,
    });

    setPermanentMessage({
      type: "error",
      message: errorMsg,
      show: true,
    });

    // Stop spinner immediately on error
    setPaymentInProgress("");
    processingRef.current = false;
  };

  const processPayment = async (token) => {
    const now = Date.now();

    // Prevent duplicate requests
    if (processingRef.current || now - lastPaymentAttemptRef.current < 5000) {
      const warningMsg =
        "Payment request blocked: Either already processing or too soon.";
      setPayementSnackBar({
        type: "warning",
        open: true,
        message: warningMsg,
      });
      setPermanentMessage({
        type: "warning",
        message: warningMsg,
        show: true,
      });
      console.warn(warningMsg);
      return;
    }

    // Clear previous messages and start processing
    clearPermanentMessage();
    processingRef.current = true;
    lastPaymentAttemptRef.current = now;
    setPaymentInProgress(token);

    // Clear any existing close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    try {
      const res = await dispatch(
        makeOnetimePayment({
          "parent-account-number": parentAccountNumber,
          "service-type":
            state && state.toUpperCase() === "FL" ? "Fiber FL" : "FIBER",
          "service-account-number": serviceAccountNumber,
          "billing-account-number": billingAccountNumber,
          "payment-token": token,
          amount: makePaymentAmount,
          source: "myaccount-website",
          "record-payment": true,
        }),
      );

      const paymentStatus = res?.payload?.data?.data?.["payment-status"];
      const isDuplicateRequest =
        res?.payload === "Duplicate request blocked within 30 second";

      if (paymentStatus === "ACCEPTED") {
        handlePaymentSuccess(makePaymentAmount);
      } else if (isDuplicateRequest) {
        const warningMsg = "Duplicate request blocked within 30 seconds.";
        setPayementSnackBar({
          type: "warning",
          open: true,
          message: warningMsg,
        });
        setPermanentMessage({
          type: "warning",
          message: warningMsg,
          show: true,
        });
        // Stop spinner for duplicate
        setPaymentInProgress("");
        processingRef.current = false;
      } else {
        const errorMessages = res?.payload?.messages;
        handlePaymentError(errorMessages);
      }
    } catch (error) {
      console.error("Payment error:", error);
      handlePaymentError(["Payment failed due to an error."]);
    }
  };

  const numberToOrdinal = (n) => {
    const ordinals = [
      "zeroth",
      "first",
      "second",
      "third",
      "fourth",
      "fifth",
      "sixth",
      "seventh",
      "eighth",
      "ninth",
      "tenth",
      "eleventh",
      "twelfth",
      "thirteenth",
      "fourteenth",
      "fifteenth",
      "sixteenth",
      "seventeenth",
      "eighteenth",
      "nineteenth",
    ];

    const tens = [
      "",
      "",
      "twentieth",
      "thirtieth",
      "fortieth",
      "fiftieth",
      "sixtieth",
      "seventieth",
      "eightieth",
      "ninetieth",
    ];

    if (n < 20) {
      return ordinals[n] || n;
    }

    if (n < 100) {
      const unit = n % 10;
      const ten = Math.floor(n / 10);
      return unit === 0
        ? tens[ten]
        : tens[ten].replace("ieth", "y") + "-" + ordinals[unit];
    }

    return n + "th";
  };

  const setAmountInTextBox = (amount) => {
    dispatch(setMakePaymentAmount(amount));
  };

  return (
    <>
      <CustomSnackbar
        openSnackbar={openSnackbar}
        severity={
          paymenProfileDataDelete?.successMessage.includes("Failed")
            ? "error"
            : "success"
        }
        speak={false}
        message={paymenProfileDataDelete?.successMessage}
        handleClose={() => {
          setOpenSnackbar(false);
          dispatch(clearSuccessMessage());
        }}
      />
      <CustomSnackbar
        openSnackbar={payementSnackBar.open}
        severity={payementSnackBar.type}
        message={payementSnackBar.message}
        handleClose={() => {
          setPayementSnackBar({
            ...payementSnackBar,
            open: false,
          });
        }}
        speak={false}
      />
      <div className="row">
        <div className="col-lg-3">
          <div className="title">
            <div className="sub-heading">Payment Summary</div>
          </div>
          <div className="modalContent">
            <div className="d-flex flex-column" style={{ width: "100%" }}>
              <label
                style={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#333",
                  marginBottom: "5px",
                }}
              >
                Amount (Should be greater than $1)
              </label>
              <br />
              <VoiceTextbox
                type="number"
                min="1"
                className="form-control"
                style={{
                  borderRadius: "8px",
                  padding: "10px",
                  fontSize: "16px",
                  border: "1px solid #ccc",
                  outline: "none",
                  width: "100%",
                }}
                command="Make Payment"
                param="Amount"
                setValue={setAmountInTextBox}
                //disabled={true}
                disabled={
                  userAccountDetails?.userLoginInfo?.userTypeCode !==
                    UserTypes.CENTRIC || paymentInProgress.length !== 0
                }
                value={makePaymentAmount}
                onChange={(e) => setAmountInTextBox(e.target.value)}
                onBlur={(e) => {
                  if (!e.target.value || parseFloat(e.target.value) < 1) {
                    e.target.value = "";
                  }
                }}
              />
            </div>
          </div>
        </div>
        <div className="col-lg-9">
          <div className="modalRightContent">
            <div className="creditDebitCard">
              <div className="title d-flex justify-content-between">
                <div className="sub-heading">
                  Use one of the saved payment method
                </div>
              </div>
              {permanentMessage.show && (
                <Alert
                  severity={permanentMessage.type}
                  sx={{ mb: 2 }}
                  onClose={
                    permanentMessage.type === "error" ||
                    permanentMessage.type === "warning"
                      ? clearPermanentMessage
                      : undefined
                  }
                >
                  {permanentMessage.message}
                  {permanentMessage.type === "success" && (
                    <>
                      <div style={{ marginTop: "8px", fontWeight: "600" }}>
                        {autoCloseEnabled
                          ? `This popup will auto close in ${countdown} seconds`
                          : "Auto-close has been stopped"}
                      </div>
                      <div
                        style={{
                          marginTop: "12px",
                          display: "flex",
                          gap: "10px",
                        }}
                      >
                        <button
                          onClick={handleCloseNow}
                          style={{
                            padding: "6px 16px",
                            backgroundColor: "#2e7d32",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          Close Now
                        </button>
                        {autoCloseEnabled && (
                          <button
                            onClick={handleStopAutoClose}
                            style={{
                              padding: "6px 16px",
                              backgroundColor: "#ed6c02",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "14px",
                              fontWeight: "500",
                            }}
                          >
                            Stop Auto-Close
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </Alert>
              )}
              <div className="modalContent">
                <div className="cardViewList">
                  {paymenProfileData?.loading ? (
                    [...Array(2)].map((_, index) => (
                      <Card key={index} sx={{ width: "30%", p: 2, mb: 2 }}>
                        <CardContent>
                          <div className="d-flex align-items-center gap-10px">
                            <Skeleton variant="square" width={80} height={80} />
                            <div>
                              <Skeleton width={150} height={20} />
                              <Skeleton width={120} height={15} />
                              <Skeleton width={80} height={15} />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  ) : paymenProfileData?.data &&
                    paymenProfileData?.data.length > 0 ? (
                    paymenProfileData?.data.map((profile, index) => {
                      const selectedCard = cardtype.find(
                        (card) => card.name === profile.type,
                      );
                      return (
                        <div
                          className="cardViewItem"
                          key={profile.token || index}
                        >
                          <div className="cardView">
                            <div className="cardNum">
                              <p>
                                ***{" "}
                                {profile["profile-account-number"].slice(-4)}
                              </p>
                            </div>
                            <div className="name">
                              <p>
                                {profile["card-holder-name"]?.toUpperCase()}
                              </p>
                              {profile["profile-description"] && (
                                <small style={{ color: "white" }}>
                                  {profile[
                                    "profile-description"
                                  ]?.toUpperCase()}
                                </small>
                              )}
                            </div>

                            {selectedCard ? (
                              <div>
                                <img
                                  src={`images/cards/${selectedCard.image}.svg`}
                                  alt={selectedCard.name}
                                />
                              </div>
                            ) : (
                              <p>{profile.type}</p>
                            )}
                            <p>
                              {profile["credit-card-expiry-date"]?.month}/
                              {profile["credit-card-expiry-date"]?.year}
                            </p>
                            {makePaymentAmount >= 1 && (
                              <div className="cvButton">
                                <VoiceButton
                                  className="btn btn-custom electric_cobalt"
                                  command="Make Payment"
                                  param="Payment Method"
                                  paramValue={numberToOrdinal(index + 1)}
                                  onClick={() => processPayment(profile.token)}
                                  disabled={paymentInProgress.length !== 0}
                                >
                                  Pay ${makePaymentAmount}
                                  {paymentInProgress.length !== 0 &&
                                    profile.token === paymentInProgress && (
                                      <CircularProgress height={20} />
                                    )}
                                </VoiceButton>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                      }}
                    >
                      <p className="text-center text-muted">No data found.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <hr />

            <div className="otherPayment">
              <div className="title">
                <div className="title d-flex justify-content-between">
                  <a
                    href="#"
                    onClick={() => {
                      setOpenCollapse(!openCollapse);
                    }}
                  >
                    + Or Add New
                    <IconButton size="small">
                      {openCollapse ? (
                        <ExpandLessIcon sx={{ color: "#3498db" }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: "#3498db" }} />
                      )}
                    </IconButton>
                  </a>
                </div>
              </div>
              <Collapse in={openCollapse} timeout="auto" unmountOnExit>
                <AddNewPaymentModal />
              </Collapse>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MakePayment;
